import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { cdnUrl } from 'commons/cdn'
import { cn } from '@/lib/utils'
import { VapCanvasPlayer } from './vap/VapCanvasPlayer'

/** 透明 VAP/图预览底色：纯黑对比度最高，浅色/彩色内容都更清晰 */
export const MEDIA_PREVIEW_BG_CLASS = 'bg-black'

interface AvatarMediaPreviewProps {
  videoUrl?: string
  imageUrl?: string
  className?: string
  /** 列表场景默认 true：仅滚动容器可见区域内加载/播放视频 */
  lazyVideo?: boolean
  videoFit?: 'contain' | 'cover'
  /** 静态图 / 视频封面图适配，默认 contain（9:16 全屏不裁上下） */
  imageFit?: 'contain' | 'cover'
}

/** 最近的纵向可滚动祖先；若容器未真正裁剪（高度随内容撑开），返回 null 改用视口 */
function getLazyVideoObserverRoot(el: Element): Element | null {
  let node = el.parentElement
  while (node && node !== document.body) {
    const oy = getComputedStyle(node).overflowY
    if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
      // 未出现滚动条/裁剪时，相对该 root 会把所有行都判成可见
      if (node.scrollHeight > node.clientHeight + 1) {
        return node
      }
      return null
    }
    node = node.parentElement
  }
  return null
}

export function AvatarMediaPreview({
  videoUrl,
  imageUrl,
  className,
  lazyVideo = true,
  videoFit = 'contain',
  imageFit = 'contain',
}: AvatarMediaPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [useImageFallback, setUseImageFallback] = useState(false)
  const [isVisible, setIsVisible] = useState(!lazyVideo)
  /** 进入过可视区后保持挂载，滚出只暂停，避免重复下载 */
  const [hasActivated, setHasActivated] = useState(!lazyVideo)

  const resolvedVideo = videoUrl?.trim() ? cdnUrl(videoUrl) : null
  const resolvedImage = imageUrl?.trim() ? cdnUrl(imageUrl) : null

  const handleVideoError = useCallback(() => {
    setUseImageFallback(true)
  }, [])

  useLayoutEffect(() => {
    setUseImageFallback(false)
    setIsVisible(!lazyVideo)
    setHasActivated(!lazyVideo)
  }, [resolvedVideo, resolvedImage, lazyVideo])

  useLayoutEffect(() => {
    if (!lazyVideo) {
      setIsVisible(true)
      setHasActivated(true)
      return
    }

    const el = containerRef.current
    if (!el) return

    const scrollRoot = getLazyVideoObserverRoot(el)
    const io = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting
        setIsVisible(visible)
        if (visible) setHasActivated(true)
      },
      { root: scrollRoot, rootMargin: '80px', threshold: 0 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [lazyVideo, resolvedVideo])

  const frameClass = cn(
    'relative h-full w-full overflow-hidden',
    MEDIA_PREVIEW_BG_CLASS,
    className,
  )
  const imageClass =
    imageFit === 'cover'
      ? 'h-full w-full object-cover object-center'
      : 'h-full w-full object-contain object-center'

  if (!resolvedVideo || useImageFallback) {
    if (!resolvedImage) return null
    return (
      <div className={frameClass}>
        <img src={resolvedImage} alt="" className={imageClass} />
      </div>
    )
  }

  const shouldMountPlayer = !lazyVideo || hasActivated
  const shouldPlayVideo = !lazyVideo || isVisible

  return (
    <div ref={containerRef} className={frameClass}>
      {shouldMountPlayer ? (
        <VapCanvasPlayer
          src={resolvedVideo}
          playing={shouldPlayVideo}
          onError={handleVideoError}
          objectFit={videoFit}
          className="h-full w-full"
        />
      ) : resolvedImage ? (
        <img src={resolvedImage} alt="" className={imageClass} />
      ) : null}
    </div>
  )
}
